import dayjs, { Dayjs } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import { theme } from 'design';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);

export const formatDateForApi = (date: string | Dayjs) => dayjs(date, ['YYYY-MM-DD', 'MM-DD-YYYY']).format('YYYY-MM-DD');

export const formatDateForUI = (date: string) => dayjs(date).format('MM/DD/YYYY');

export const formatDateAbbreviated = (date: string) => dayjs(date).format('MMM D, YYYY');

export const formatDateAbbreviatedWithDay = (date: string) => dayjs(date).format('ddd D MMM YYYY');

export const formatDateForUILongFormat = (date: string | Dayjs, time?: boolean, utc?: boolean, windowWidth?: number) => {
  const day = utc ? dayjs.utc(date) : dayjs(date);
  const monthFormat = windowWidth <= theme.breakpoint.small ? 'MMM' : 'MMMM';
  return day.format(`${monthFormat} Do, YYYY${time ? ' hh:mm A' : ''}`);
};

export const formatDateLongWithDay = (date: string, time?: boolean) => dayjs(date, ['YYYY-MM-DD', 'MM-DD-YYYY']).format(`dddd, MMMM Do, YYYY${time ? ' hh:mm A' : ''}`);

export const formatDateAndTime = (date: string) => dayjs(date).local().format('MM/DD/YYYY h:mm A z');

export const formatTime = (time24h: string, stripLeadingZero = false) => dayjs(time24h, 'HH:mm').format(`${stripLeadingZero ? 'h' : 'hh'}:mm A`);

export const formatTimeRange = (start: string, end: string) => `${formatTime(start, true)} - ${formatTime(end, true)}`;

export const getTimeWithZone = (date: string) => dayjs(date).format('h:mm A z');

export const getTimeWithoutZone = (date: string, tz?: string) => dayjs.utc(date).tz(tz).format('h:mm A');

export const getDateTimeWithoutZone = (date: string, tz?: string) => dayjs.utc(date).tz(tz).format('MM/DD/YYYY h:mm A');

export const getCurrentBusinessDay = (tz: string, midnightOffset: number): Dayjs => {
  const currentTime = dayjs().toDate();
  return dayjs(currentTime).utc().subtract(midnightOffset, 'second').tz(tz);
};

/**
 * Renders a human-friendly, DST-agnostic label for an IANA timezone.
 *
 * @param {string} tz - The IANA timezone string (e.g. "America/Los_Angeles").
 * @returns {string} The generic long name (e.g. "Pacific Time"), or the raw `tz` if it can't be resolved.
 */
export const formatTimezone = (tz?: string): string => {
  if (!tz) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'longGeneric' }).formatToParts(new Date());
    return parts.find((part) => part.type === 'timeZoneName')?.value || tz;
  } catch {
    return tz;
  }
};

export const convertTo12HourTime = (val: string): string => {
  const time = val.split(':', 2);
  const hours = Number(time[0]);

  if (hours > 12) {
    const newHours = hours - 12;
    return `${newHours < 10 ? `0${newHours}` : newHours}:${time[1] || ''}`;
  }
  if (hours === 0) {
    return `12:${time[1] || ''}`;
  }

  return val;
};

/**
 * Calculates the time difference between two date-time strings and formats it as HH:mm.
 *
 * @param {string} startDate - The start date-time in a valid string format (e.g., "YYYY-MM-DDTHH:mm:ss").
 * @param {string} endDate - The end date-time in a valid string format (e.g., "YYYY-MM-DDTHH:mm:ss").
 * @returns {string} The formatted time difference in HH:mm, prefixed with '-' if the end time is earlier than the start time.
 */
export const calculateTimeDifference = (startDate: string, endDate: string): string => {
  // Parse the input dates
  const d1 = new Date(startDate);
  const d2 = new Date(endDate);

  if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime())) {
    throw new Error('Invalid date format. Please provide valid date strings.');
  }

  // Calculate the difference in milliseconds
  const diff = d1.getTime() - d2.getTime();

  // Get the absolute difference in hours and minutes
  const totalMinutes = Math.abs(Math.floor(diff / (1000 * 60)));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  // Format as HH:MM
  const formatted = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

  // Add a negative sign if the first date is after the second
  return diff > 0 ? `-${formatted}` : formatted;
};
